/**
 * @fileoverview
 * 管理者認証ゲートとログイン画面。
 *
 * アプリ初期化時に `POST /v1/admin/auth/refresh` を 1 回試行してセッション復元を行い、
 * 成功すればエディタ本体を、失敗すればログイン画面を表示する。
 * ログイン画面では `invalid_credentials` と `rate_limited` (Retry-After 付き) を
 * 区別して表示する。パスワードや Access Token はログへ出力しない。
 */

import {
	Button,
	Card,
	Flex,
	Heading,
	Spinner,
	Text,
	TextField,
} from "@radix-ui/themes";
import { useAtomValue } from "jotai";
import {
	type FC,
	type FormEvent,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	AdminApiError,
	adminLogin,
	initializeAdminSession,
} from "$/modules/admin-auth/api/client";
import { adminAuthStatusAtom } from "$/modules/admin-auth/states";

const LoginScreen = () => {
	const { t } = useTranslation();
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const canSubmit = username.trim() !== "" && password !== "" && !submitting;

	const describeLoginError = useCallback(
		(err: unknown): string => {
			if (err instanceof AdminApiError) {
				if (err.code === "invalid_credentials") {
					return t(
						"adminAuth.login.errors.invalidCredentials",
						"ユーザー名またはパスワードが正しくありません",
					);
				}
				if (err.status === 429) {
					return err.retryAfter !== null
						? t(
								"adminAuth.login.errors.rateLimited",
								"試行回数が上限に達しました。{seconds} 秒ほど待ってから再試行してください",
								{ seconds: err.retryAfter },
							)
						: t(
								"adminAuth.login.errors.rateLimitedRetryLater",
								"試行回数が上限に達しました。しばらくしてから再試行してください",
							);
				}
				if (err.code === "validation_error") {
					return t(
						"adminAuth.login.errors.validation",
						"ユーザー名とパスワードを入力してください",
					);
				}
				if (err.status >= 500) {
					return t(
						"adminAuth.login.errors.serverError",
						"サーバーでエラーが発生しました。時間をおいて再試行してください",
					);
				}
			}
			return t(
				"adminAuth.login.errors.network",
				"ログインに失敗しました。ネットワーク接続を確認してください",
			);
		},
		[t],
	);

	const onSubmit = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			if (!canSubmit) return;
			setSubmitting(true);
			setErrorMessage(null);
			try {
				// 成功すると adminAuthStatusAtom が authenticated になり、ゲートが切り替わる。
				await adminLogin(username.trim(), password);
			} catch (err) {
				setErrorMessage(describeLoginError(err));
			} finally {
				setSubmitting(false);
			}
		},
		[canSubmit, describeLoginError, password, username],
	);

	return (
		<Card size="3" style={{ width: "min(360px, calc(100vw - 32px))" }}>
			<form onSubmit={onSubmit}>
				<Flex direction="column" gap="4">
					<Flex direction="column" gap="1">
						<Heading size="5">
							{t("adminAuth.login.title", "管理者ログイン")}
						</Heading>
						<Text size="2" color="gray">
							{t(
								"adminAuth.login.description",
								"TTML Editor を利用するには、sakanova の管理者アカウントでログインしてください。",
							)}
						</Text>
					</Flex>

					<Flex direction="column" gap="1">
						<Text as="label" size="2" weight="medium" htmlFor="admin-username">
							{t("adminAuth.login.username", "ユーザー名")}
						</Text>
						<TextField.Root
							id="admin-username"
							value={username}
							onChange={(e) => setUsername(e.target.value)}
							autoComplete="username"
							disabled={submitting}
							autoFocus
						/>
					</Flex>

					<Flex direction="column" gap="1">
						<Text as="label" size="2" weight="medium" htmlFor="admin-password">
							{t("adminAuth.login.password", "パスワード")}
						</Text>
						<TextField.Root
							id="admin-password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoComplete="current-password"
							disabled={submitting}
						/>
					</Flex>

					{errorMessage && (
						<Text size="2" color="red">
							{errorMessage}
						</Text>
					)}

					<Button type="submit" disabled={!canSubmit}>
						{submitting ? (
							<>
								<Spinner size="1" />
								{t("adminAuth.login.loggingIn", "ログイン中…")}
							</>
						) : (
							t("adminAuth.login.submit", "ログイン")
						)}
					</Button>
				</Flex>
			</form>
		</Card>
	);
};

/**
 * @description 認証ゲート。認証済みなら子要素 (エディタ本体) を表示し、
 * 復元中はスピナー、未認証ならログイン画面を表示する。
 */
export const AdminAuthGate: FC<PropsWithChildren> = ({ children }) => {
	const status = useAtomValue(adminAuthStatusAtom);
	const { t } = useTranslation();

	useEffect(() => {
		void initializeAdminSession();
	}, []);

	if (status === "authenticated") {
		return <>{children}</>;
	}

	return (
		<Flex align="center" justify="center" height="100vh" p="4">
			{status === "initializing" ? (
				<Flex align="center" gap="2">
					<Spinner size="2" />
					<Text size="2" color="gray">
						{t("adminAuth.restoring", "セッションを確認しています…")}
					</Text>
				</Flex>
			) : (
				<LoginScreen />
			)}
		</Flex>
	);
};
